/**
 * Vehicle.ts — An occasional car or van driving along a road.
 *
 * A vehicle moves along a road at constant speed and yaws to face its heading.
 * It can occasionally turn at junctions, using its blinkers (indicators) to signal
 * the turn ahead of time and while executing a smooth 90-degree curve across the junction.
 *
 * ## Signalling
 *
 * The indicator has to come on far enough ahead to be *read* as a signal rather
 * than as the turn itself. That means two things working together, and both of
 * them are easy to lose:
 *
 *   - the warning is a **time** (`VEHICLES.BLINKER_LEAD_MS`) converted to a
 *     distance at the vehicle's own speed, not a fixed distance that silently
 *     becomes a shorter warning every time traffic gets faster;
 *   - a vehicle only ever signals for **the next junction it will reach**
 *     (`nearestJunctionAhead`), because a lead that long can reach past one
 *     crossing to the one behind it.
 *
 * A vehicle that plans no turn at a junction stays quiet through it and rolls
 * again at the next one.
 *
 * Its collision shape is the axis-aligned footprint returned by `bounds()`, sized
 * to the vehicle's travel orientation and kind.
 */
import * as THREE from 'three';
import { VEHICLES, ROADS } from '../config/constants';
import { Entity } from './Entity';
import { createVehicle } from '../render/models';
import { isStreet, spanReaches, type RoadSegment, type VehicleSpawn } from '../systems/roads';
import { facingYaw } from './locomotion';
import type { Junction } from '../systems/junctions';
import type { VehicleKind } from '../types';
import type { Rect } from '../systems/level';

export type TurnDir = 'left' | 'right';
export type BlinkerState = 'left' | 'right' | 'none';

export interface TurnArc {
  cx: number;
  cy: number;
  r: number;
  startTheta: number;
  endTheta: number;
  currentTheta: number;
  omega: number;
  newVx: number;
  newVy: number;
  targetX: number;
  targetY: number;
}

export function getAvailableTurns(
  vx: number,
  vy: number,
  junction: Junction,
  roads: readonly RoadSegment[],
): TurnDir[] {
  const turns: TurnDir[] = [];

  // Eastbound (+x)
  if (vx > 0 && Math.abs(vy) < 1) {
    if (roads.some((s) => isStreet(s) && s.orient === 'v' && s.pos === junction.x && spanReaches(s, junction.y - ROADS.WIDTH))) {
      turns.push('left');
    }
    if (roads.some((s) => isStreet(s) && s.orient === 'v' && s.pos === junction.x && spanReaches(s, junction.y + ROADS.WIDTH))) {
      turns.push('right');
    }
  }
  // Westbound (-x)
  else if (vx < 0 && Math.abs(vy) < 1) {
    if (roads.some((s) => isStreet(s) && s.orient === 'v' && s.pos === junction.x && spanReaches(s, junction.y + ROADS.WIDTH))) {
      turns.push('left');
    }
    if (roads.some((s) => isStreet(s) && s.orient === 'v' && s.pos === junction.x && spanReaches(s, junction.y - ROADS.WIDTH))) {
      turns.push('right');
    }
  }
  // Southbound (+y)
  else if (vy > 0 && Math.abs(vx) < 1) {
    if (roads.some((s) => isStreet(s) && s.orient === 'h' && s.pos === junction.y && spanReaches(s, junction.x + ROADS.WIDTH))) {
      turns.push('left');
    }
    if (roads.some((s) => isStreet(s) && s.orient === 'h' && s.pos === junction.y && spanReaches(s, junction.x - ROADS.WIDTH))) {
      turns.push('right');
    }
  }
  // Northbound (-y)
  else if (vy < 0 && Math.abs(vx) < 1) {
    if (roads.some((s) => isStreet(s) && s.orient === 'h' && s.pos === junction.y && spanReaches(s, junction.x - ROADS.WIDTH))) {
      turns.push('left');
    }
    if (roads.some((s) => isStreet(s) && s.orient === 'h' && s.pos === junction.y && spanReaches(s, junction.x + ROADS.WIDTH))) {
      turns.push('right');
    }
  }

  return turns;
}

export function setupTurnArc(
  vx: number,
  vy: number,
  turnDir: TurnDir,
  junction: Junction,
): TurnArc | null {
  const speed = Math.hypot(vx, vy);
  if (speed < 1) return null;
  const R = ROADS.WIDTH / 4;
  const J = junction;

  // Eastbound (+x)
  if (vx > 0 && Math.abs(vy) < 1) {
    if (turnDir === 'left') {
      const r = R;
      return {
        cx: J.x - 2 * R,
        cy: J.y - 2 * R,
        r,
        startTheta: Math.PI / 2,
        endTheta: 0,
        currentTheta: Math.PI / 2,
        omega: -speed / r,
        newVx: 0,
        newVy: -speed,
        targetX: J.x - R,
        targetY: J.y - 2 * R,
      };
    } else {
      const r = 2 * R;
      return {
        cx: J.x - R,
        cy: J.y + R,
        r,
        startTheta: -Math.PI / 2,
        endTheta: 0,
        currentTheta: -Math.PI / 2,
        omega: speed / r,
        newVx: 0,
        newVy: speed,
        targetX: J.x + R,
        targetY: J.y + R,
      };
    }
  }
  // Westbound (-x)
  else if (vx < 0 && Math.abs(vy) < 1) {
    if (turnDir === 'left') {
      const r = R;
      return {
        cx: J.x + 2 * R,
        cy: J.y + 2 * R,
        r,
        startTheta: -Math.PI / 2,
        endTheta: -Math.PI,
        currentTheta: -Math.PI / 2,
        omega: -speed / r,
        newVx: 0,
        newVy: speed,
        targetX: J.x + R,
        targetY: J.y + 2 * R,
      };
    } else {
      const r = 2 * R;
      return {
        cx: J.x + R,
        cy: J.y - R,
        r,
        startTheta: Math.PI / 2,
        endTheta: Math.PI,
        currentTheta: Math.PI / 2,
        omega: speed / r,
        newVx: 0,
        newVy: -speed,
        targetX: J.x - R,
        targetY: J.y - R,
      };
    }
  }
  // Southbound (+y)
  else if (vy > 0 && Math.abs(vx) < 1) {
    if (turnDir === 'left') {
      const r = R;
      return {
        cx: J.x + 2 * R,
        cy: J.y - 2 * R,
        r,
        startTheta: Math.PI,
        endTheta: Math.PI / 2,
        currentTheta: Math.PI,
        omega: -speed / r,
        newVx: speed,
        newVy: 0,
        targetX: J.x + 2 * R,
        targetY: J.y - R,
      };
    } else {
      const r = 2 * R;
      return {
        cx: J.x - R,
        cy: J.y - R,
        r,
        startTheta: 0,
        endTheta: Math.PI / 2,
        currentTheta: 0,
        omega: speed / r,
        newVx: -speed,
        newVy: 0,
        targetX: J.x - R,
        targetY: J.y + R,
      };
    }
  }
  // Northbound (-y)
  else if (vy < 0 && Math.abs(vx) < 1) {
    if (turnDir === 'left') {
      const r = R;
      return {
        cx: J.x - 2 * R,
        cy: J.y + 2 * R,
        r,
        startTheta: 0,
        endTheta: -Math.PI / 2,
        currentTheta: 0,
        omega: -speed / r,
        newVx: -speed,
        newVy: 0,
        targetX: J.x - 2 * R,
        targetY: J.y + R,
      };
    } else {
      const r = 2 * R;
      return {
        cx: J.x + R,
        cy: J.y + R,
        r,
        startTheta: Math.PI,
        endTheta: 1.5 * Math.PI,
        currentTheta: Math.PI,
        omega: speed / r,
        newVx: speed,
        newVy: 0,
        targetX: J.x + R,
        targetY: J.y - R,
      };
    }
  }

  return null;
}

export class Vehicle extends Entity {
  readonly kind: VehicleKind;
  blinker: BlinkerState = 'none';
  private blinkerTimerMs = 0;
  private turnState: 'straight' | 'turning' = 'straight';
  private plannedTurn: { turnDir: TurnDir; junction: Junction } | null = null;
  private turnArc: TurnArc | null = null;
  private visitedJunctions = new Set<string>();

  constructor(spawn: VehicleSpawn, kind: VehicleKind = 'car') {
    const tint = VEHICLES.TINTS[Math.floor(Math.random() * VEHICLES.TINTS.length)];
    const group = createVehicle(kind, tint);
    super(spawn.x, spawn.y, VEHICLES.WIDTH / 2, group);

    this.kind = kind;
    this.vel.x = spawn.vx;
    this.vel.y = spawn.vy;
    this.yaw = Math.atan2(spawn.vx, spawn.vy);
    this.syncMesh();
  }

  /** Ground-plane footprint used for the player/dog hit test. */
  bounds(): Rect {
    const len = VEHICLES.KINDS[this.kind].LENGTH;
    const horizontal = Math.abs(this.vel.x) >= Math.abs(this.vel.y);
    const w = horizontal ? len : VEHICLES.WIDTH;
    const h = horizontal ? VEHICLES.WIDTH : len;
    return { x: this.pos.x - w / 2, y: this.pos.y - h / 2, w, h };
  }

  /** Update motion, checking for upcoming junction turns and executing arc turns. */
  updateVehicle(dt: number, junctions: readonly Junction[] = [], roads: readonly RoadSegment[] = []): void {
    if (this.turnState === 'turning' && this.turnArc) {
      const arc = this.turnArc;
      arc.currentTheta += arc.omega * dt;
      const finished =
        arc.omega > 0
          ? arc.currentTheta >= arc.endTheta - 1e-4
          : arc.currentTheta <= arc.endTheta + 1e-4;

      if (finished) {
        this.pos.x = arc.targetX;
        this.pos.y = arc.targetY;
        this.vel.x = arc.newVx;
        this.vel.y = arc.newVy;
        this.turnState = 'straight';
        this.turnArc = null;
        this.plannedTurn = null;
        this.blinker = 'none';
        this.yaw = facingYaw(this.vel.x, this.vel.y, this.yaw);
      } else {
        this.pos.x = arc.cx + arc.r * Math.cos(arc.currentTheta);
        this.pos.y = arc.cy + arc.r * Math.sin(arc.currentTheta);
        this.vel.x = -arc.r * Math.sin(arc.currentTheta) * arc.omega;
        this.vel.y = arc.r * Math.cos(arc.currentTheta) * arc.omega;
        this.yaw = facingYaw(this.vel.x, this.vel.y, this.yaw);
      }
    } else {
      // Straight driving: check for junction approaches
      this.checkJunctions(junctions, roads);
      if (this.plannedTurn) {
        const R = ROADS.WIDTH / 4;
        const J = this.plannedTurn.junction;
        let shouldStart = false;
        if (this.vel.x > 0 && Math.abs(this.vel.y) < 1) {
          shouldStart = this.plannedTurn.turnDir === 'left' ? this.pos.x >= J.x - 2 * R : this.pos.x >= J.x - R;
        } else if (this.vel.x < 0 && Math.abs(this.vel.y) < 1) {
          shouldStart = this.plannedTurn.turnDir === 'left' ? this.pos.x <= J.x + 2 * R : this.pos.x <= J.x + R;
        } else if (this.vel.y > 0 && Math.abs(this.vel.x) < 1) {
          shouldStart = this.plannedTurn.turnDir === 'left' ? this.pos.y >= J.y - 2 * R : this.pos.y >= J.y - R;
        } else if (this.vel.y < 0 && Math.abs(this.vel.x) < 1) {
          shouldStart = this.plannedTurn.turnDir === 'left' ? this.pos.y <= J.y + 2 * R : this.pos.y <= J.y + R;
        }

        if (shouldStart) {
          const arc = setupTurnArc(this.vel.x, this.vel.y, this.plannedTurn.turnDir, J);
          if (arc) {
            this.turnState = 'turning';
            this.turnArc = arc;
          }
        }
      }
      this.integrate(dt);
    }

    this.blinkerTimerMs += dt * 1000;
    this.syncMesh();
  }

  /**
   * How far ahead this vehicle starts indicating, in world units.
   *
   * Derived from its own speed so the signal is always `BLINKER_LEAD_MS` of
   * driving rather than a fixed distance that means different things to a
   * fast level and a slow one.
   */
  private noticeDistance(): number {
    return Math.hypot(this.vel.x, this.vel.y) * (VEHICLES.BLINKER_LEAD_MS / 1000);
  }

  /**
   * The next junction this vehicle will reach, and how far off it is.
   *
   * "Next" is load-bearing: it is the nearest one **ahead and in our own lane**,
   * not merely the first in the list that happens to be within range. Once the
   * signal starts two seconds out it can easily reach past one crossing to the
   * one behind it (blocks get as short as ~160 units, and two seconds at
   * `SPEED` is ~340), and picking the wrong one of those two puts a car through
   * a junction with its indicator going and then turning at the *next* one —
   * which is worse than not indicating at all.
   *
   * The distance comes back measured to the junction **centre**, and a junction
   * counts as ahead until we are clear of its whole box — deliberately, rather
   * than counting it done at the turn-in point `2R` before it. Otherwise a
   * vehicle still crossing a junction it declined sees that box as already
   * behind it, looks on to the next crossing, and lights up its indicator while
   * sitting on the tarmac of the one it is driving straight through.
   *
   * Returns `null` when there is nothing ahead of us on this road — the vehicle
   * is mid-turn, stopped, or on a road with no crossings left.
   */
  private nearestJunctionAhead(junctions: readonly Junction[]): { junction: Junction; dist: number } | null {
    const R = ROADS.WIDTH / 4;
    const half = ROADS.WIDTH / 2;
    let best: { junction: Junction; dist: number } | null = null;

    for (const j of junctions) {
      let dist = -Infinity;
      // Each arm pairs the lane we must be sitting in (UK left-hand driving,
      // see roads.ts `laneOffset`) with the distance to the junction centre.
      if (this.vel.x > 0 && Math.abs(this.vel.y) < 1 && Math.abs(this.pos.y - (j.y - R)) < 5) {
        dist = j.x - this.pos.x;
      } else if (this.vel.x < 0 && Math.abs(this.vel.y) < 1 && Math.abs(this.pos.y - (j.y + R)) < 5) {
        dist = this.pos.x - j.x;
      } else if (this.vel.y > 0 && Math.abs(this.vel.x) < 1 && Math.abs(this.pos.x - (j.x + R)) < 5) {
        dist = j.y - this.pos.y;
      } else if (this.vel.y < 0 && Math.abs(this.vel.x) < 1 && Math.abs(this.pos.x - (j.x - R)) < 5) {
        dist = this.pos.y - j.y;
      }

      if (dist > -half && (best === null || dist < best.dist)) best = { junction: j, dist };
    }

    return best;
  }

  private checkJunctions(junctions: readonly Junction[], roads: readonly RoadSegment[]): void {
    if (this.plannedTurn || junctions.length === 0 || roads.length === 0) return;

    const next = this.nearestJunctionAhead(junctions);
    if (!next) return;

    // Only ever consider the very next crossing. If we have already rolled for
    // it and are still short of it, we stay quiet rather than looking past it
    // at the one beyond — see `nearestJunctionAhead`.
    const key = `${next.junction.x},${next.junction.y}`;
    if (this.visitedJunctions.has(key)) return;

    // The lead is counted to where the wheel actually moves — the turn-in point
    // for a left, `2R` short of the centre — not to the centre itself.
    const turnIn = next.dist - 2 * (ROADS.WIDTH / 4);
    if (turnIn > this.noticeDistance()) return;

    this.visitedJunctions.add(key);
    const available = getAvailableTurns(this.vel.x, this.vel.y, next.junction, roads);
    if (available.length > 0 && Math.random() < VEHICLES.TURN_CHANCE) {
      const turnDir = available[Math.floor(Math.random() * available.length)];
      this.plannedTurn = { turnDir, junction: next.junction };
      this.blinker = turnDir;
      // Restart the flash cycle so the signal opens *lit*. The timer free-runs
      // from spawn, so without this a turn planned mid-off-phase throws away
      // the first quarter-second of the warning — the one part of it that has
      // to land.
      this.blinkerTimerMs = 0;
    }
  }

  override syncMesh(): void {
    super.syncMesh();
    this.updateBlinkers();
  }

  private updateBlinkers(): void {
    const leftBlinkers = (this.object.userData.leftBlinkers as THREE.Mesh[]) ?? [];
    const rightBlinkers = (this.object.userData.rightBlinkers as THREE.Mesh[]) ?? [];

    if (this.blinker === 'none') {
      leftBlinkers.forEach((b) => (b.visible = false));
      rightBlinkers.forEach((b) => (b.visible = false));
    } else {
      const flashOn = Math.floor(this.blinkerTimerMs / VEHICLES.BLINKER_INTERVAL_MS) % 2 === 0;
      if (this.blinker === 'left') {
        rightBlinkers.forEach((b) => (b.visible = false));
        leftBlinkers.forEach((b) => (b.visible = flashOn));
      } else if (this.blinker === 'right') {
        leftBlinkers.forEach((b) => (b.visible = false));
        rightBlinkers.forEach((b) => (b.visible = flashOn));
      }
    }
  }

  /** Remove the vehicle's meshes from the scene. */
  dispose(scene: THREE.Scene): void {
    scene.remove(this.object);
  }
}
